import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import {
  Feed,
  Post,
  User,
  WorkflowPostStatus,
  WorkflowStagePost,
} from '../../../models/core.models';
import {
  ImageFallbacks,
  ImagePlaceholders,
  NomAlgorithm,
  NomPostType,
  NomSentiment,
} from 'src/app/core/constants';
import {
  PostAlgorithm,
  PostAlgorithmService,
} from 'src/app/core/services/post-algorithm.service';
import {
  removeAllItemsBySource,
  removePostFromFeed,
} from 'src/app/core/actions/feed.actions';
import {
  setPostSentiment,
  togglePostParentThread,
  togglePostSelect,
  transitionToWorkflowStage,
} from '../../../actions/post.actions';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import { GalleryViewerComponent } from '../../gallery-viewer/gallery-viewer.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { ObjectEditorService } from '../../../services/object-editor.service';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  isShadowPostId,
  realPostId,
} from 'src/app/shared/utils/shadow-post.utils';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { readableTextColor } from 'src/app/shared/utils/color-utils';
import { UserFacadeService } from '@services/user-facade.service';
import { environment } from '@env/environment';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { take } from 'rxjs';

@Component({
  selector: 'app-post-card',
  templateUrl: './post-card.component.html',
  styleUrls: ['./post-card.component.scss'],
  standalone: false,
})
export class PostCardComponent implements OnChanges, OnInit {
  @ViewChild('galleryContainer', { read: ViewContainerRef })
  galleryContainer: ViewContainerRef;
  @ViewChild('postCategoryTree') postCategoryTree: ElementRef;
  faXTwitter = faXTwitter;

  @ViewChild('card', { static: false }) card!: ElementRef<HTMLElement>;

  @HostBinding('class.is-reply') get isReply(): boolean {
    return (
      this.showThreads && this.post?.has_parent_thread && !this.post?.is_parent
    );
  }

  @HostBinding('class.is-thread-parent') get isThreadParent(): boolean {
    return this.showThreads && this.post?.is_parent;
  }

  // * readable text color for the custom post background (matches header behavior)
  get foregroundColor(): string | null {
    return this.backgroundColor
      ? readableTextColor(this.backgroundColor)
      : null;
  }

  @Input() post: Post;
  @Input() feed: Feed;

  /**
   * Shadow-derived flags, recomputed in ngOnChanges when `post`/`selectable`
   * change rather than on every change-detection tick (these are read from the
   * template inside *ngIf). See shadow-post.utils.ts.
   *
   * - `realPostId`: the real post id, stripped of any thread "shadow" suffix.
   *   Use this (never `post.id`) for backend-bound per-post mutations — shadow
   *   posts carry a synthetic `${realId}__shadow__${leafId}` id the backend
   *   cannot resolve.
   * - `isShadow`: true when this card is a display-only thread "shadow" copy.
   *   Shadow cards exist purely to visualize the thread: they expose no
   *   select/edit affordances (see the `isShadow` guards below and in the
   *   template).
   * - `isSelectable`: shadow posts and pure thread-parent posts are
   *   display-only, so they never render a checkbox / respond to selection.
   */
  realPostId: string;
  isShadow = false;
  isSelectable = true;

  private updateShadowFlags(): void {
    this.realPostId = realPostId(this.post?.id);
    this.isShadow = isShadowPostId(this.post?.id);
    this.isSelectable =
      this.selectable && !this.isShadow && !this.post?.is_parent;
  }
  @Input() user: User;
  @Input() feedId: string;
  @Input() objectTypeId: number = null;
  @Input() viewMode: 'default' | 'compact' | 'gallery' = 'default';
  // Shows a drag-handle affordance on the compact card so users understand the
  // card can be reordered (drag-sorting is wired up by the parent list).
  @Input() sortable: boolean = false;
  @Input() showThreads: boolean = false;
  @Input() selectable: boolean = true;
  @Input() highlighted: boolean = false;
  @Input() allowRelatedPosts: boolean = false;
  @Input() removable: boolean = true;
  @Input() customTogglePostSelect?: (post: Post) => void; // New input for custom function
  @Input() customRemovePost?: (post: Post) => void;
  @Input() currentLanguage: string;
  @Input() viewConfig: PropertyViewConfig[];
  @Input() disableTools: boolean = false;
  @Input() isStoryMode: boolean = false;
  @Input() timeShown: string = 'post_time_published_relative';
  @Input() loadingAlgorithmPostIds: string[];
  @Input() appliedAlgorithmPostIds: string[];
  // * optional custom post background (e.g. POST_LIST widget's post_background_color)
  @Input() backgroundColor: string | null = null;
  @Output() togglePostParent: EventEmitter<Post> = new EventEmitter();
  @Output() imageLoaded = new EventEmitter<void>(); // Event emitter

  @Output() togglePostDetails = new EventEmitter<{
    isDetailsView: boolean;
    postId: string;
  }>();
  @Output() requestToggleThread = new EventEmitter<{ postId: string }>();
  readonly loadingImage = ImagePlaceholders.spinner;
  readonly avatarFallback = ImageFallbacks.avatar;

  algorithms: PostAlgorithm[] = [];
  availableCardPropertyViewConfig: PropertyViewConfig[] = [];
  postPropertyViewConfig: PropertyViewConfig[] = [];

  // * Contains the configuration for the post tools as keys
  // * The key is the path to the tool
  // * The value is the boolean indicating if the tool should be displayed
  postToolsConfig: { [key: string]: boolean } = {};

  // * Contains the configuration for the post properties as keys
  // * The key is the path to the property
  // * The value is a boolean indicating if the property should be displayed
  // * The value is set to true if the property is selected in the view configuration
  postPropertyConfig: { [key: string]: boolean } = {};

  categoryTreeVisible = false;
  workflows: { [workflowId: string]: WorkflowPostStatus };
  workflowId: string;
  workflowStages: WorkflowStagePost[];

  translateUrl: string = '';
  debugUrl: string = '';
  debugSourceUrl: string = '';

  NomSentiment = NomSentiment;
  AppComponents = AppFeatures;
  UserAccessLevel = UserAccessLevel;
  sourceTitle: string = '';
  companyCategoryNames: string;
  sentimentClassMap = {
    [NomSentiment.NEUTRAL]: 'neutral',
    [NomSentiment.POSITIVE]: 'positive',
    [NomSentiment.NEGATIVE]: 'negative',
  };

  environment = environment;

  scoreIcon: IconProp;
  displayedTime!: string;

  /**
   * True when the post carries a screen capture (`post_screen_capture` is an
   * absolute URL). Recomputed on `post` changes rather than read straight from
   * the template, since it gates an *ngIf marker in the card header.
   */
  hasScreenCapture = false;

  postCompactContent: string = '';
  compactExpanded: boolean = false;

  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private algorithmService: PostAlgorithmService,
    private editService: ObjectEditorService,
    private transateService: TranslateService,
    private logger: LoggerService,
    protected userFacade: UserFacadeService,
  ) {}

  ngOnInit() {
    const sortedAlgorithms = this.algorithmService
      .getAlgorithmsPerUser(this.user, this.feed)
      .sort((a, b) => a.group?.localeCompare(b.group));

    sortedAlgorithms.forEach((algorithm) => {
      this.algorithms.push(algorithm);
    });
    this.workflows = this.post?.workflows;
    this.translateUrl = this.transformToTranslateURL(this.post.url);

    this.debugUrl =
      environment.sensikaSysAppDomain + '/test/DataObject_Post/' + this.post.id;
    this.debugSourceUrl =
      environment.sensikaSysAppDomain +
      '/#/?source_id=' +
      this.post.source.object_id +
      '&section_id=' +
      this.post.section.id;

    if (this.workflows) {
      this.processStages();
    }

    this.sourceTitle = this.truncateText(this.post.source.title, 32);
    this.companyCategoryNames =
      this.feed?.company_categories
        ?.map((category: any) => category.company_category_name)
        .join(' / ') || '';

    this.scoreIcon = this.getScoreIcon(this.post.completeness_score);
    this.hasScreenCapture = !!this.post.post_screen_capture;
    this.configureCompactPostDisplay();
    this.updateViewConfiguration();

    // Determine displayed time based on input
  }

  private configureCompactPostDisplay() {
    if (this.viewMode === 'compact') {
      switch (this.post.post_type_id) {
        case NomPostType.OnlineArticle: // Text Post
          this.postCompactContent = this.truncateText(this.post.title, 200);
          break;

        default:
          this.postCompactContent = this.truncateText(this.post.content, 100);
      }
    }
  }

  private updateViewConfiguration() {
    this.postPropertyViewConfig = [];
    this.postToolsConfig = {};
    this.postPropertyConfig = {};

    this.viewConfig?.forEach((item) => {
      if (item.selected) {
        if (item.visibility?.includes('tools')) {
          this.postToolsConfig[item.path] = true;
        } else {
          this.postPropertyConfig[item.path] = true;
          if (item.path !== 'content') {
            this.postPropertyViewConfig.push({
              label: item.label,
              value: this.getPropertyByPath(this.post, item.path as string),
              propertyUri: item.propertyUri,
              icon: item.icon,
              visibility: item.visibility,
              path: item.path,
            });
          }
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['post']) {
      this.workflows = changes['post'].currentValue.workflows;
      this.processStages();
      this.hasScreenCapture =
        !!changes['post'].currentValue.post_screen_capture;
    }

    if (changes['post'] || changes['selectable']) {
      this.updateShadowFlags();
    }

    if (changes['viewConfig']) {
      this.updateViewConfiguration();
    }
  }

  processStages(): void {
    // Iterate over the object keys
    for (const key in this.workflows) {
      if (this.workflows.hasOwnProperty(key)) {
        const item = this.workflows[key];

        // Access the stages array
        this.workflowId = key;
        this.workflowStages = item.stages;
      }
    }
  }

  changeWorkflowStage(stage: WorkflowStagePost): void {
    if (this.isShadow) return; // display-only thread copy — not editable

    this.store.dispatch(
      transitionToWorkflowStage({
        postId: this.realPostId,
        workflowId: this.workflowId,
        workflowStageId: stage.id,
        feedId: this.feed.id,
      }),
    );
  }

  onContentClick(event: MouseEvent) {
    event.stopPropagation();

    // Social posts (X, Facebook, etc.) navigate to the original post via the
    // anchor's href; every other type toggles selection instead.
    if (this.post.type === 'social' && this.post.url) {
      return;
    }

    event.preventDefault();
    this.togglePostSelect();
  }

  togglePostSelect() {
    if (this.isSelectable) {
      if (this.customTogglePostSelect) {
        // If a custom function is provided, use it
        this.customTogglePostSelect(this.post);
      } else {
        // default behavior if no custom function is provided
        this.store.dispatch(
          togglePostSelect({
            postId: this.post.id,
            feedId: this.feedId,
            selected: !this.post.selected,
          }),
        );
      }
    }
  }

  toggleCategoryTree(event?: MouseEvent) {
    event?.stopPropagation();
    if (this.isShadow) return; // display-only thread copy — not editable
    this.categoryTreeVisible = !this.categoryTreeVisible;
  }

  /**
   * Toggles the display of post details.
   */
  toggleDetails() {
    this.togglePostDetails.emit({
      isDetailsView: true,
      // Emit the real id — shadow ids are display-only and unresolvable
      // outside the feed's post map.
      postId: this.realPostId,
    });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  /**
   * Opens a popup to edit the categories of the post.
   * @returns {void}
   */
  editCategories() {
    event.stopPropagation();
    if (this.isShadow) return; // display-only thread copy — not editable

    // Use the real post (shadow id stripped) so the algorithm targets the
    // underlying post and not a display-only thread copy.
    const realPost = { ...this.post, id: this.realPostId };
    this.algorithmService.runPostAlgorithm(
      this.feedId,
      realPost,
      NomAlgorithm.SET_CATEGORIES,
      { post: realPost },
    );
  }

  editRelated() {
    if (this.isShadow) return; // display-only thread copy — not editable

    this.editService.editRelated(
      { ...this.post, id: this.realPostId },
      this.feedId,
      this.feed?.object_type_id ?? this.objectTypeId,
    );
  }

  sharePost() {
    this.editService.sharePost(this.post);
  }

  togglePostParentCard(post: Post) {
    this.togglePostParent.emit(post);
  }

  onImageLoaded() {
    this.imageLoaded.emit();
  }

  editSentiment() {
    if (this.isShadow) return; // display-only thread copy — not editable

    this.editService.editSentiment(
      this.feedId,
      this.realPostId,
      this.post.post_sentiment_algorithm_list[0].sentiment_id,
      this.post.sentiment_value_auto,
    );
  }

  setSentiment(sentimentValue: number) {
    if (this.isShadow) return; // display-only thread copy — not editable

    // Sentiment Endpoint requires -1/0/1 values for sentiment rather than NomSentiment values - 1/2/3

    const sentimentMap = {
      [NomSentiment.NEGATIVE]: -1,
      [NomSentiment.NEUTRAL]: 0,
      [NomSentiment.POSITIVE]: 1,
    };

    const mappedValue = sentimentMap[sentimentValue];

    this.store.dispatch(
      setPostSentiment({
        feedId: this.feedId,
        postId: this.realPostId,
        sentiment: mappedValue,
      }),
    );
  }

  removeFromFeed() {
    if (this.isShadow) return; // display-only thread copy — not editable

    const message = this.transateService.instant(
      'post.confirm.are-you-sure-you-want-to-delete-this-item-from-the-feed',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            removePostFromFeed({
              postId: this.realPostId,
              feedId: this.feedId,
            }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          this.logger.debug(
            'removeFromFeed',
            `Action cancelled: ${event.reason}`,
          );
        }
      },
    );
  }

  runAlgorithm(postAlgorithm: PostAlgorithm) {
    if (this.isShadow) return; // display-only thread copy — not editable

    // Use the real post (shadow id stripped) so the algorithm targets the
    // underlying post and not a display-only thread copy.
    const realPost = { ...this.post, id: this.realPostId };
    this.algorithmService.runPostAlgorithm(
      this.feedId,
      realPost,
      postAlgorithm.id,
      {
        post: realPost,
      },
    );
  }

  getPropertyByPath(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  getValue(path: string) {
    return this.getPropertyByPath(this.post, path);
  }

  openGallery() {
    // Clear the container
    this.galleryContainer.clear();

    // Create the component and add to the view
    const componentRef = this.galleryContainer.createComponent(
      GalleryViewerComponent,
    );
    const imageUrls = this.post.attachments
      .filter((attachment) => attachment.type === 'image')
      .map((attachment) => attachment.image_src);
    // Pass the image URL to the gallery component
    componentRef.instance.imageUrls = imageUrls;

    // subscribe to the component's close event to remove it when closed
    componentRef.instance.closed
      .pipe(take(1)) // auto-unsubscribe after first emit
      .subscribe(() => {
        componentRef.destroy();
      });
  }

  // @HostListener('document:click', ['$event'])
  // onClick(event: MouseEvent) {
  //   if (
  //     this.postCategoryTree &&
  //     !this.postCategoryTree.nativeElement.contains(event.target)
  //   ) {
  //     this.categoryTreeVisible = false;
  //     this.logger.debug('onClick', 'Category tree closed');
  //   }
  // }

  transformToTranslateURL(url: string): string {
    // Parse the input URL
    try {
      const parsedURL = new URL(url);

      // Replace dots in the hostname with dashes
      const transformedHost =
        parsedURL.hostname.replace(/\./g, '-') + '.translate.goog';

      // Build the translated URL
      const translatedURL = new URL(parsedURL.toString());
      translatedURL.hostname = transformedHost;

      // Add Google Translate parameters
      translatedURL.searchParams.set('_x_tr_sl', 'auto'); // Auto-detect source language
      translatedURL.searchParams.set('_x_tr_tl', this.currentLanguage); // Translate to Current Language
      translatedURL.searchParams.set('_x_tr_hl', this.currentLanguage); // Set the Google Translate interface language to Current Language

      return translatedURL.toString();
    } catch (error) {
      this.logger.error(
        'transformToTranslateURL',
        'Invalid URL provided: ' + url,
      );
      return url; // Return the original URL if parsing fails
    }
  }

  truncateText(value: string, limit: number = 20): string {
    const ellipsis = '...';
    if (!value) return '';
    return value.length > limit ? value.substring(0, limit) + ellipsis : value;
  }

  removeSourcePosts(source) {
    const objectIds = [source.object_id];

    const message = this.transateService.instant(
      'post.confirm.are-you-sure-you-want-to-remove-all-posts-from-this-source',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            removeAllItemsBySource({
              feedId: this.feed.id,
              objectId: objectIds,
              objectClassId: source.object_class_id,
            }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          this.logger.debug(
            'removeAllSourcePosts',
            `Action cancelled: ${event.reason}`,
          );
        }
      },
    );
  }

  trackById(index: number, item: any): string {
    return item.id;
  }

  getScoreIcon(score: number): IconProp {
    if (score <= 25) {
      return ['far', 'star'];
    } else if (score > 25 && score <= 75) {
      return ['fas', 'star-half-stroke'];
    } else {
      return ['fas', 'star'];
    }
  }

  // call this instead of dispatching inside the child
  toggleParentThread(post: Post, ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.requestToggleThread.emit({ postId: post.id });
  }
}
